import qs, { ParsedQs } from 'qs'

import getLogger from 'shared/logger'
import { isProductionEnv, LOCAL_HOST, REFINEMENT_DIMENSION } from 'shared/constants'
import { DEFAULT_PAGING_SIZES } from '~/app/store/paging/constants'
import { matchGalleryParams } from '~/app/utils/matchGalleryParams'
import config from 'config'
import { UserContext } from '@vp/ubik-fragment-types'
import { sanitize } from '~/shared/xss'
/* eslint-disable @typescript-eslint/no-explicit-any */
//TODO MIGRATION T011: Fix eslint errors

/**
 * Converts a value in multiple formats to an array.
 *   if the value is a string, returns an array with the strong as the only item
 *   if the value is null or undefined, returns an empty array
 * @param value
 */
function getArrayFromValue(value?: Gallery.Models.Url.ValidQsValue): string[] {
  if (!value) {
    return []
  }

  return (Array.isArray(value) ? value : [value])
    .flatMap((item) => {
      if (item === null) {
        return null
      }
      return typeof item === 'string' ? item.split(',') : item.toString()
    })
    .filter((item): item is string => item !== null)
}

/**
 * Converts a value in multiple formats to a single string
 *   if the value is an array, only the first item is returned
 * @param value
 */
function getStringFromValue(value: Gallery.Models.Url.ValidQsValue): Gallery.Models.Url.ValidParsedQsValue<string> {
  if (value === null || value === undefined) {
    return value
  }

  const ret = Array.isArray(value) ? value[0] : value

  if (ret === null) {
    return null
  }

  return typeof ret === 'string' ? ret : ret.toString()
}

/**
 *
 * @param value
 */
function getNumberFromQuery(value?: Gallery.Models.Url.ValidQsValue): Gallery.Models.Url.ValidParsedQsValue<number> {
  if (value === null || value === undefined) {
    return value
  }

  return value ? parseInt(getStringFromValue(value) as string, 10) : null
}

/**
 *
 * @param url
 */
export function parseUrl(url: string): Gallery.Models.Url.ParsedUrl {
  if (url.startsWith('?')) {
    return {
      url: '',
      query: qs.parse(url, { ignoreQueryPrefix: true }),
    }
  }
  const urlObj = new URL(url)
  const baseUrl = urlObj.href.split('?')[0]
  const queryString = urlObj.search

  return {
    url: baseUrl,
    query: qs.parse(queryString, { ignoreQueryPrefix: true }),
  }
}

export function stringifyUrl({ url, query }: Gallery.Models.Url.ParsedUrl) {
  const qsString = query ? qs.stringify(query) : ''

  return qsString ? `${url}?${qsString}` : url
}

export function stringifyArrayFormatRepeat(params: Record<string, unknown>) {
  return qs.stringify(params, { arrayFormat: 'repeat' })
}

/**
 * Resolve Quantity from two possible variations on the variable value in the qs
 * @param query
 */
export function getAllQuantityData(value?: string): { total: Gallery.Models.Url.Quantity.Total; data: Gallery.Models.Url.Quantity.Data } {
  const result: { total: Gallery.Models.Url.Quantity.Total; data: Gallery.Models.Url.Quantity.Data } = {
    total: 0,
    data: 0,
  }

  if (!value || typeof value !== 'string') return result

  const valueAsNumber = Number(value)

  if (!Number.isNaN(valueAsNumber) && valueAsNumber > 0) {
    result.total = valueAsNumber
    result.data = valueAsNumber
  } else if (Number.isNaN(valueAsNumber)) {
    try {
      const parsed = JSON.parse(value || '{}')

      const total = Object.values(parsed).reduce((acc: number, curr) => acc + (typeof curr === 'number' ? curr : 0), 0)

      if (total > 0) {
        result.total = total
        result.data = parsed
      }
    } catch (e) {
      getLogger().warn('Invalid quantity value', e as Error)
      // Do nothing, this is an invalid quantity value, use the default result
    }
  }

  return result
}

/**
 * Returns an object parsed from a JSON-encoded query param under `root`.
 *
 * - If `query[root]` is `undefined`, not a `string`, or contains invalid JSON,
 *   an empty object (`{} as T`) is returned.
 * - Arrays and already-parsed objects are not handled; this function is intended
 *   **only** for the "JSON-in-a-string" query param case (e.g.`?renderPropsOverride[TilePreviewSize]=Large`).
 *
 * @template T
 * @param {qs.ParsedQs} query - Result of `qs.parse(...)`.
 * @param {string} root - Query key expected to contain a JSON string.
 * @returns {T} The parsed object, or an empty object (`{} as T`) on absence/parse failure.
 *
 * @example
 * // URL: /page?renderPropsOverride[TilePreviewSize]=Large
 * getObjectFromValue<State.RenderPropsState>(parsed, 'renderPropsOverride');
 * // -> { TilePreviewSize: "Large" }
 */
function getObjectFromValue<T = Util.StringDictionary<string | number>>(query: qs.ParsedQs, root: string): T {
  const raw = query[root]

  if (!raw || typeof raw !== 'string') {
    return {} as T
  }

  try {
    return JSON.parse(raw) as T
  } catch {
    return {} as T
  }
}

/**
 * Converts a parsed QS (qs library) value into an array of string
 *   if the value is a string or an array of string, returns an array
 *   if the value is undefined or any type of ParsedQs (unhandled case at the moment), returns an empty array
 * @param value
 * @param root
 */
function getArrayFromQSValue(value: qs.ParsedQs, root: string): string[] {
  if (!value) {
    return []
  }

  const rootValue = value[root]

  if (typeof rootValue === 'string') {
    return rootValue.split(',')
  }

  return Array.isArray(rootValue) && rootValue.length
    ? rootValue.map((item: any) => (typeof item === 'string' ? item : null)).filter((item): item is string => item !== null)
    : []
}

/**
 * Converts query parameter values into an array of JSON objects
 * Handles rawSets which should be JSON-encoded strings that don't get split on commas
 * @param value Query parameter value from query string
 */
function getObjectArrayFromValue(value: Gallery.Models.Url.ValidQsValue): Gallery.Models.Url.ValidParsedQsValue<Gallery.Models.Url.JSONObject[]> {
  if (!value) {
    return null
  }

  // Handle the case where value is already an array of strings (complete JSON strings)
  // or a single string that needs to be parsed
  const jsonStrings = Array.isArray(value) ? value : [value]

  const validJsonStrings = jsonStrings
    .map((item) => (item === null ? null : typeof item === 'string' ? item : item.toString()))
    .filter((item): item is string => item !== null)

  if (validJsonStrings.length === 0) {
    return null
  }

  try {
    return validJsonStrings.map((jsonString) => JSON.parse(jsonString))
  } catch (e) {
    getLogger().warn('Failed to parse object array', e as Error)
    return null
  }
}

/**
 * Apply defaults to the query string whenever the specified elements are undefined
 *
 * @param rawQuery
 */
function applyDefaultsForQuery(rawQuery: ParsedQs): ParsedQs {
  return {
    useConstraints: rawQuery.useConstraints === undefined ? 'true' : rawQuery.useConstraints,
    ...rawQuery,
  }
}

export function getBooleanFromValue(value: Gallery.Models.Url.ValidQsValue): Gallery.Models.Url.ValidParsedQsValue<boolean> {
  return getStringFromValue(value)?.toLowerCase() === 'true'
}

/**
 * Parses a gallery url to get the gallery context
 * @param inputUrl
 */

export function getGalleryContextFromQueryString(inputUrl: string): Gallery.Models.Url.QueryStringState {
  const { query: rawQuery } = parseUrl(inputUrl)
  const parsedQS = qs.parse(inputUrl.split('?')[1], { ignoreQueryPrefix: true })

  const query = applyDefaultsForQuery(rawQuery)
  let pageSize = getNumberFromQuery(query.pageSize)

  if (pageSize) {
    pageSize = Math.min(pageSize, Math.max(...DEFAULT_PAGING_SIZES))
  }

  const categoriesRefinement = getArrayFromValue(query.categories).filter((cat) => Number(cat) > 0)

  const quantityInfo = getAllQuantityData(parsedQS.qty as string)

  return {
    query,
    bypassApproval: getBooleanFromValue(query.bypassApproval),
    debug: getBooleanFromValue(query.debug),
    noCache: getBooleanFromValue(query.noCache),
    useConstraints: getBooleanFromValue(query.useConstraints),
    paging: {
      page: getNumberFromQuery(query.page),
      pageSize,
    },
    quantity: quantityInfo.total,
    quantityData: quantityInfo.data ?? quantityInfo.total,
    refinements: {
      [REFINEMENT_DIMENSION.ATTRIBUTE_PLURAL]: getArrayFromQSValue(parsedQS, 'attributes'),
      [REFINEMENT_DIMENSION.CATEGORY_PLURAL]: categoriesRefinement,
      [REFINEMENT_DIMENSION.KEYWORD]: parsedQS.keyword as string,
      [REFINEMENT_DIMENSION.COLLECTION]: getStringFromValue(query.collection),
    },
    renderPropsOverride: getObjectFromValue<State.RenderPropsState>(parsedQS, 'renderPropsOverride'),
    forcedRankingStrategyOverride: getStringFromValue(query.forcedRankingStrategyOverride),
    selectedOptions: getObjectFromValue(parsedQS, 'selectedOptions'),
    forceVariation: getArrayFromValue(query.forceVariation),
    quickViewId: getStringFromValue(query.qv) || matchGalleryParams(inputUrl).designId,
    quickViewIdFromSearch: getStringFromValue(query.quickView),
    pageId: getStringFromValue(query.pageId),
    templatePurposes: getArrayFromQSValue(parsedQS, 'templatePurposes') as State.TEMPLATE_PURPOSES[],
    igpLevel: getNumberFromQuery(query.igpLevel),
    sortingStrategy: getStringFromValue(query.sortingStrategy),
    searchBackend: getStringFromValue(query.searchBackend),
    source: getStringFromValue(query.source),
    designCreationTypes: getArrayFromValue(query.designCreationTypes),
    aspExperimentFlags: getObjectFromValue(parsedQS, 'aspExperimentFlags'),
    isSimilarProduct: getBooleanFromValue(query.isSimilarProduct),
    takeOverQV: getBooleanFromValue(query.takeOverQV),
    selectedDesignId: getStringFromValue(query.selectedDesignId),
    showSets: getBooleanFromValue(query.showSets),
    rawSets: getObjectArrayFromValue(parsedQS.rawSets as Gallery.Models.Url.ValidQsValue),
    dpcId: getStringFromValue(query.dpcId),
    designConcept: getStringFromValue(query.designConcept),
  }
}

/**
 * Add a parameter key and value to an existing url
 * @param fullURL
 * @param paramKey
 * @param paramValue
 */
export function addQueryParamToUrl(fullURL: string, paramKey: string, paramValue: string): string {
  const { url, query = {} } = parseUrl(fullURL)

  query[paramKey] = paramValue

  return stringifyUrl({ url, query })
}

/**
 * Add a `page` query param to an existing canonical url
 * @param baseUrl
 * @param page
 */
export const adjustBaseUrl = (baseUrl: string | undefined, page: number | null | undefined): string | undefined => {
  if (!baseUrl || !page) {
    return baseUrl
  }

  const url = new URL(baseUrl)

  url.searchParams.set('page', page.toString())

  return url.toString()
}

export enum QueryParamKey {
  name = 'name',
  categories = 'categories',
  bypassapproval = 'bypassApproval',
  nocache = 'noCache',
  useconstraints = 'useConstraints',
  pagesize = 'pageSize',
  qty = 'qty',
  attributes = 'attributes',
  keyword = 'keyword',
  collection = 'collection',
  renderpropsoverride = 'renderPropsOverride',
  forcedrankingstrategyoverride = 'forcedRankingStrategyOverride',
  selectedoptions = 'selectedOptions',
  forcevariation = 'forceVariation',
  quickview = 'quickView',
  pageid = 'pageId',
  igplevel = 'igpLevel',
  templatepurposes = 'templatePurposes',
  sortingstrategy = 'sortingStrategy',
  searchbackend = 'searchBackend',
  source = 'source',
  designcreationtypes = 'designCreationTypes',
  aspexperimentflags = 'aspExperimentFlags',
  issimilarproduct = 'isSimilarProduct',
  takeoverqv = 'takeOverQV',
  selecteddesignid = 'selectedDesignId',
  showsets = 'showSets',
  rawsets = 'rawSets',
  forwardedpath = 'forwardedPath',
  productid = 'productId',
  manifestsig = 'manifestSig',
  manifesturl = 'manifestUrl',
  urlid = 'urlId',
  dpcid = 'dpcId',
  designconcept = 'designConcept',
}

export const queryParamsDictionary: Record<string, QueryParamKey> = Object.entries(QueryParamKey).reduce(
  (acc, [lowerCase, camelCase]) => {
    acc[lowerCase] = camelCase
    return acc
  },
  {} as Record<string, QueryParamKey>,
)

/**
 * Sanitizes all values in an object to prevent XSS
 */
export const sanitizeObjectValues = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {}

  Object.entries(obj).forEach(([key, value]) => {
    result[key] = sanitize(value)
  })

  return result
}

/**
 * Transforms an object with query strings from lower case to camel case
 */
export const transformQueryStringsToCamelCase = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {}

  Object.entries(obj).forEach(([key, value]) => {
    const camelCaseKey = queryParamsDictionary[key.toLowerCase()] || key
    if (!key.includes('-')) {
      result[camelCaseKey] = value
    }
  })

  return result
}

/**
 * Transforms query strings to camelCase and sanitizes values
 */
export const transformQueryStrings = (obj: Record<string, any> = {}): Record<string, any> => {
  return sanitizeObjectValues(transformQueryStringsToCamelCase(obj))
}

export const buildRequestUrl = (locale: string, forwardedPath: string, queryStrings: UserContext['queryStrings']) => {
  if (!forwardedPath) {
    forwardedPath = ''
  } else if (!forwardedPath.startsWith('/')) {
    forwardedPath = `/${forwardedPath}`
  }

  const host = isProductionEnv ? config.client.vistaprintRoot[locale.toLowerCase()] : LOCAL_HOST

  const queryString = stringifyArrayFormatRepeat(queryStrings || {})

  return `${host}${forwardedPath}${queryString ? `?${queryString}` : ''}`
}
